/**
 * Created by Rodrigo Quesada on 15/12/16.
 */
import {Component, forwardRef, HostBinding, Injector, Input, OnInit} from '@angular/core';
import {Response} from '@angular/http';
import {FileService} from '../../../files/service/file.service';
import {BehaviorSubject, Observable} from 'rxjs';
import {combineLatest} from 'rxjs/observable/combineLatest';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, NgModel} from '@angular/forms';
import {noop} from '../../../../common/functions/functions.util';
import {FileLocation} from '../../../files/service/file-location.class';

// region NG_VALUE_ACCESSOR
const MY_FILE_INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => FileInputComponent),
    multi: true
};
// endregion

let nextUniqueId = 0;

@Component({
    selector: 'my-file-input',
    template: require('./file-input.component.html'),
    styles: [require('./file-input.component.scss')],
    providers: [MY_FILE_INPUT_CONTROL_VALUE_ACCESSOR]
})
export class FileInputComponent implements ControlValueAccessor, OnInit {

    readonly id = `my-file-input-${++nextUniqueId}`;

    @HostBinding('class.uploading') uploading: boolean = false;
    @HostBinding('class.downloading') downloading: boolean = false;
    @HostBinding('class.deleting') deleting: boolean = false;

    private _fileAttached: boolean = false;

    @HostBinding('class.file-attached') get fileAttached(): boolean { return this._fileAttached; };

    set fileAttached(value: boolean) {
        if (value !== this._fileAttached) {
            this._onChangeCallback(value);
            this._fileAttached = value;
        }
    }

    // region Input
    @Input() name: string | undefined;

    private readonly _errorMessageSubject$ = new BehaviorSubject<string | undefined>(undefined);

    @Input() set errorMessage(value: string | undefined) { this._errorMessageSubject$.next(value); }

    private readonly _associatedInputErrorMessageSubject$ = new BehaviorSubject<string | undefined>(undefined);

    @Input() set associatedInputErrorMessage(value: string | undefined) { this._associatedInputErrorMessageSubject$.next(value); }

    @Input() errorMessagePosition: string = 'right';

    errorMessages$: Observable<Array<string>> = combineLatest(this._errorMessageSubject$, this._associatedInputErrorMessageSubject$)
        .map((messages) => Stream.from(messages).filter(it => !!it).toArray());

    private readonly _fileLocation$ = new BehaviorSubject<FileLocation | undefined>(undefined);

    @Input() set fileLocation(value: FileLocation) {
        this._fileLocation$.next(value);
    }

    // endregion

    ngModel: NgModel;

    constructor(private readonly _fileService: FileService, private readonly _injector: Injector) {}

    ngOnInit() {
        this.ngModel = this._injector.get(NgModel);
        this._fileLocation$
            .filter(it => !!it)
            .switchMap(location => this._fileService.exists(location!))
            .subscribe(it => {
                this._fileAttached = it;
                this._onChangeCallback(it, true);
            });
    }

    uploadFile(event) {
        let files = event.target.files;
        if (files.length) {
            this.uploading = true;
            this._fileService.upload(files, this._fileLocation$.getValue()!).subscribe((res: Response) => {
                event.target.value = null;
                this.uploading = false;
                if (res.status === 200) {
                    this.fileAttached = true;
                }
            });
        }
    }

    downloadFile() {
        if (!this.downloading) {
            this.downloading = true;
            this._fileService.download(this._fileLocation$.getValue()!)
                .finally(() => this.downloading = false)
                .subscribe();
        }
    }

    removeFile() {
        if (!this.deleting) {
            this.deleting = true;
            this._fileService.remove(this._fileLocation$.getValue()!)
                .finally(() => this.deleting = false)
                .subscribe((res: Response) => {
                    if (res.status === 200) {
                        this.fileAttached = false;
                    }
                });
        }
    }

    // region ControlValueAccessor
    /** Callback registered via registerOnTouched (ControlValueAccessor) */
    private _onTouchedCallback: () => void = noop;
    /** Callback registered via registerOnChange (ControlValueAccessor) */
    private _onChangeCallback: (value: boolean, bypassChangeDetection?: boolean) => void = noop;

    writeValue(value: any): void { }

    registerOnChange(fn: any): void {
        this._onChangeCallback = (value, bypassChangeDetection) => {
            const stringValue = value ? 'file attached' : undefined;
            if (bypassChangeDetection) this.ngModel.control.setValue(stringValue);
            else fn(stringValue);
        };
    }

    registerOnTouched(fn: any): void {
        this._onTouchedCallback = fn;
    }

    // endregion
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/components/forms/controls/file/file-input.component.ts